// ShowSupportTickets.jsx
import React, { useEffect, useState, useRef } from "react";
import EmptyState from "./EmptyState";
import useSupportTickets from "./useSupportTicket";
import consumer from "./consumer";
import {
  closeSupportTicket,
  submitSupportReview,
  updateSupportTicketStatus
} from "../../../services/supportTicket";

const openStatuses = ["Active", "Pending", "Open", "In_progress"];
const closedStatuses = ["Closed", "Resolved"];

const formatTime = (date) =>
  new Date(date).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

const formatTimeAgo = (date) => {
  const seconds = Math.floor((new Date() - new Date(date)) / 1000);
  const intervals = [
    { label: "year", seconds: 31536000 },
    { label: "month", seconds: 2592000 },
    { label: "day", seconds: 86400 },
    { label: "hour", seconds: 3600 },
    { label: "minute", seconds: 60 },
  ];
  for (const interval of intervals) {
    const count = Math.floor(seconds / interval.seconds);
    if (count >= 1)
      return `${count} ${interval.label}${count > 1 ? "s" : ""} ago`;
  }
  return "just now";
};

const ShowSupportTickets = () => {
  // UI state
  const [activeTab, setActiveTab] = useState("Open");
  const [currentTicketId, setCurrentTicketId] = useState(null);
  const [message, setMessage] = useState("");
  const [tickets, setTickets] = useState([]);
  const [copied, setCopied] = useState(false);

  // Review modal state
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedRating, setSelectedRating] = useState(0);
  const [reviewComment, setReviewComment] = useState("");
  const [reviewTicketId, setReviewTicketId] = useState(null);
  const [closingTicketId, setClosingTicketId] = useState(null);
  const [hoverRating, setHoverRating] = useState(0);

  // Per-ticket follow-up state
  const [ticketFollowUpModes, setTicketFollowUpModes] = useState({});
  const [ticketFollowUpOptions, setTicketFollowUpOptions] = useState({});
  const [ticketYesNoPrompt, setTicketYesNoPrompt] = useState({});
  const [ticketAllowClose, setTicketAllowClose] = useState({});

  // Simplified automation options
  const automationOptionList = [
    { label: "Close Ticket", allowClosure: true },
  ];

  // Refs
  const messagesContainerRef = useRef();
  const channelRef = useRef(null);

  const { getSupportTickets } = useSupportTickets();

  useEffect(() => {
    refreshTickets();
  }, []);

  const refreshTickets = async () => {
    try {
      const response = await getSupportTickets();
      const normalized = response.map((ticket) => ({
        id: ticket.id,
        subject: ticket.title,
        status: ticket.status === "In_progress" ? "Pending" : ticket.status,
        updated_at: new Date(ticket.dateTime),
        created_at: new Date(ticket.createdAt || ticket.dateTime),
        category: ticket.category,
        preview: ticket.preview,
        reviewRating: ticket.review_and_rating,
        messages: (ticket.fullContent || []).map((msg) => ({
          sender:
            (msg.sender || "").toLowerCase() === "you" ? "user" : "support",
          text: msg.text,
          name: msg.name,
          timestamp: new Date(`${msg.time}, 2025`).toISOString(),
        })),
        review: ticket.review || null,
        priority: ticket.priority || "Medium",
      }));
      setTickets(normalized);
    } catch (error) {
      console.error("Failed to fetch support tickets:", error);
    }
  };

  const filteredTickets = tickets.filter((ticket) =>
    activeTab === "Open"
      ? openStatuses.includes(ticket.status)
      : closedStatuses.includes(ticket.status)
  );

  const currentTicket =
    filteredTickets.find((t) => t.id === currentTicketId);

  useEffect(() => {
    if (!currentTicketId) return;

    const subscription = consumer.subscriptions.create(
      { channel: "SupportTicketChannel", ticket_id: currentTicketId },
      {
        received(data) {
          if (data.sender_type === "User") {
            setTicketFollowUpModes((prev) => ({
              ...prev,
              [currentTicketId]: "automation",
            }));
            setTicketFollowUpOptions((prev) => ({
              ...prev,
              [currentTicketId]: automationOptionList,
            }));
            setTicketYesNoPrompt((prev) => ({
              ...prev,
              [currentTicketId]: true,
            }));
            return;
          }

          if (data.message) {
            setTickets((prev) =>
              prev.map((ticket) => {
                if (ticket.id !== currentTicketId) return ticket;

                const newMsg = {
                  sender:
                    data.sender_type?.toLowerCase() === "internaluser"
                      ? "support"
                      : "user",
                  text:
                    (data.message?.trim && data.message.trim()) ||
                    "No description provided.",
                  timestamp: new Date().toISOString(),
                };

                const alreadyExists = ticket.messages.some(
                  (m) => m.text === newMsg.text && m.sender === newMsg.sender
                );
                if (alreadyExists) return ticket;

                return {
                  ...ticket,
                  messages: [...ticket.messages, newMsg],
                  updated_at: new Date(),
                };
              })
            );
          }
        },
      }
    );

    channelRef.current = subscription;

    return () => {
      try {
        consumer.subscriptions.remove(subscription);
      } catch (e) {
        // ignore
      }
    };
  }, [currentTicketId]);

  useEffect(() => {
    tickets.forEach((ticket) => {
      if (ticket.status === "Resolved" && !ticketFollowUpModes[ticket.id]) {
        setTicketFollowUpModes((prev) => ({
          ...prev,
          [ticket.id]: "automation",
        }));
        setTicketFollowUpOptions((prev) => ({
          ...prev,
          [ticket.id]: automationOptionList,
        }));
        setTicketYesNoPrompt((prev) => ({ ...prev, [ticket.id]: true }));
      }
    });
  }, [tickets]);

  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop =
        messagesContainerRef.current.scrollHeight;
    }
  }, [currentTicket?.messages?.length]);

  const sendMessage = () => {
    if (!message.trim() || !channelRef.current || !currentTicketId) return;

    const msg = {
      sender: "user",
      text: message,
      ticket_id: currentTicketId,
      timestamp: new Date().toISOString(),
    };

    setTickets((prev) =>
      prev.map((ticket) => {
        if (ticket.id !== currentTicketId) return ticket;
        return {
          ...ticket,
          messages: [...ticket.messages, msg],
          updated_at: new Date(),
        };
      })
    );

    channelRef.current.send({
      action: "receive",
      ticket_id: currentTicketId,
      message: msg,
    });

    setMessage("");

    setTicketFollowUpModes((prev) => ({
      ...prev,
      [currentTicketId]: "manual",
    }));
    setTicketYesNoPrompt((prev) => ({ ...prev, [currentTicketId]: false }));
  };

  const handleAutomationOptionClick = (ticketId, optionObj) => {
    setTickets((prev) =>
      prev.map((ticket) => {
        if (ticket.id !== ticketId) return ticket;
        return {
          ...ticket,
          messages: [
            ...ticket.messages,
            {
              sender: "user",
              text: optionObj.label,
              timestamp: new Date().toISOString(),
            },
          ],
          updated_at: new Date(),
        };
      })
    );

    if (channelRef.current) {
      try {
        channelRef.current.send({
          action: "receive",
          ticket_id: ticketId,
          message: { sender: "user", text: optionObj.label },
        });
      } catch (e) {
        // swallow
      }
    }

    if (optionObj.label === "Close Ticket") {
      handleCloseTicket(ticketId);
      return;
    }

    if (optionObj.label === "Need more help") {
      setTicketFollowUpModes((prev) => ({ ...prev, [ticketId]: "manual" }));
      setTicketYesNoPrompt((prev) => ({ ...prev, [ticketId]: false }));
    }

    setTicketAllowClose((prev) => ({
      ...prev,
      [ticketId]: !!optionObj.allowClosure,
    }));
  };

  const handleCloseTicket = async (ticketIdArg) => {
    const ticketId = ticketIdArg || currentTicketId;
    if (!ticketId) return;
    setClosingTicketId(ticketId);
    try {
      await closeSupportTicket(ticketId);
      await new Promise((resolve) => setTimeout(resolve, 2000));
      await refreshTickets();
      setActiveTab("Closed");
      setCurrentTicketId(ticketId);
      setReviewTicketId(ticketId);
      setShowReviewModal(true);
      setTicketFollowUpModes((prev) => {
        const copy = { ...prev };
        delete copy[ticketId];
        return copy;
      });
      setTicketFollowUpOptions((prev) => {
        const copy = { ...prev };
        delete copy[ticketId];
        return copy;
      });
      setTicketYesNoPrompt((prev) => ({ ...prev, [ticketId]: false }));
      setTicketAllowClose((prev) => ({ ...prev, [ticketId]: false }));
    } catch (err) {
      console.error("Failed to close ticket", err);
    } finally {
      setClosingTicketId(null);
    }
  };

  const handleReopenTicket = async (ticketId) => {
    try {
      await updateSupportTicketStatus(ticketId);
      await refreshTickets();
      setTicketYesNoPrompt((prev) => ({ ...prev, [ticketId]: false }));
    } catch (err) {
      console.error("Failed to reopen ticket", err);
    }
  };

  const handleSubmitReview = async () => {
    if (!reviewTicketId) return;
    if (selectedRating === 0) {
      alert("Please select a rating before submitting.");
      return;
    }
    try {
      await submitSupportReview({
        ticket_id: reviewTicketId,
        rating: selectedRating,
        comment: reviewComment,
      });
      setSelectedRating(0);
      setReviewComment("");
      setHoverRating(0);
      setReviewTicketId(null);
      setShowReviewModal(false);
      await refreshTickets();
    } catch (err) {
      console.error("Failed to submit review", err);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const renderActionsForTicket = (ticket) => {
    if (!ticket) return null;

    if (openStatuses.includes(ticket.status)) {
      return (
        <div className="input-group mb-2 flex-column flex-sm-row">
          <textarea
            className="form-control rounded-start-pill"
            placeholder="Type a message..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                sendMessage();
              }
            }}
            rows={2}
            style={{ resize: "none", padding: "0px 10px" , alignContent:"center" }}
          />
          <button
            className="btn btn-primary rounded-end-pill flex-shrink-0"
            onClick={sendMessage}
            style={{ minWidth: "100px" }}
          >
            Send
          </button>
        </div>
      );
    }

    if (ticket.status === "Resolved") {
      const mode = ticketFollowUpModes[ticket.id] || null;
      const yesNoVisible = ticketYesNoPrompt[ticket.id] ?? true;

      if (yesNoVisible) {
        return (
          <div className="text-center my-3">
            <p className="fw-semibold mb-2">
              Are you satisfied with the resolution?
            </p>
            <div className="d-flex justify-content-center gap-2">
              <button
                className="btn btn-outline-success btn-sm rounded-pill fw-semibold px-3"
                onClick={() => {
                  // On Yes - just show close button (no immediate review)
                  setTicketYesNoPrompt((prev) => ({
                    ...prev,
                    [ticket.id]: false,
                  }));
                  setTicketAllowClose((prev) => ({
                    ...prev,
                    [ticket.id]: true,
                  }));
                }}
              >
                Yes
              </button>
              <button
                className="btn btn-outline-danger btn-sm rounded-pill fw-semibold px-3"
                onClick={async () => {
                  // On No - reopen ticket
                  await handleReopenTicket(ticket.id);
                  setTicketYesNoPrompt((prev) => ({
                    ...prev,
                    [ticket.id]: false,
                  }));
                }}
              >
                No
              </button>
            </div>
          </div>
        );
      }

      // If user clicked Yes but hasn't closed yet
      if (ticketAllowClose[ticket.id]) {
        return (
          <div className="text-center my-3">
            <button
              className="btn btn-outline-danger rounded-pill"
              onClick={() => handleCloseTicket(ticket.id)}
              disabled={closingTicketId === ticket.id}
            >
              {closingTicketId === ticket.id ? "Closing..." : "Close Ticket"}
            </button>
          </div>
        );
      }

      return null;
    }

    if (ticket.review) {
      return (
        <div className="p-3 border rounded-3 bg-light">
          <h6>Your Review</h6>
          <div>
            <strong>Rating:</strong> {ticket.review.rating} / 5
          </div>
          <div className="mt-2">{ticket.review.comment}</div>
        </div>
      );
    }

    return null;
  };

  return (
    <div
      className="container-fluid p-0 d-flex flex-column flex-md-row vh-100 bg-light"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {/* Sidebar */}
      <aside
        className="border-end bg-white shadow-sm flex-shrink-0"
        style={{
          width: "100%",
          maxWidth: 360,
          height: "93vh",
          overflow: "hidden",
        }}
      >
        <div className="d-flex justify-content-between align-items-center p-3 border-bottom">
          <h5 className="fw-bold mb-0 text-dark">Tickets</h5>
          <a
            className="btn btn-sm btn-outline-primary rounded-pill px-3"
            data-bs-toggle="modal"
            data-bs-target="#supportModal"
          >
            <i className="bi bi-plus-circle me-1"></i>New
          </a>
        </div>

        <div className="btn-group w-100 p-2">
          <button
            className={`btn ${
              activeTab === "Open" ? "btn-primary" : "btn-outline-primary"
            }`}
            onClick={() => setActiveTab("Open")}
          >
            Open
          </button>
          <button
            className={`btn ${
              activeTab === "Closed" ? "btn-secondary" : "btn-outline-secondary"
            }`}
            onClick={() => setActiveTab("Closed")}
          >
            Closed
          </button>
        </div>

        <div className="overflow-auto p-2" style={{ height: "90vh" }}>
          {filteredTickets.map((ticket) => (
            <div
              key={ticket.id}
              onClick={() => {
                setCurrentTicketId(ticket.id);
                setMessage("");
              }}
              className={`p-3 mb-2 rounded-3 shadow-sm ${
                ticket.id === currentTicketId
                  ? "bg-primary text-white"
                  : "bg-light"
              }`}
              style={{ cursor: "pointer", transition: "all 0.2s ease-in-out" }}
            >
              <div className="d-flex justify-content-between align-items-center mb-1">
                <div
                  className="fw-semibold text-truncate"
                  style={{ maxWidth: "70%" }}
                >
                  {ticket.subject}
                </div>
                <span
                  className={`badge ${
                    ticket.status === "Open" ? "bg-success" : "bg-secondary"
                  }`}
                >
                  {ticket.status}
                </span>
              </div>
              <small
                className={
                  ticket.id === currentTicketId ? "text-light" : "text-muted"
                }
              >
                {formatTimeAgo(ticket.updated_at)}
              </small>
            </div>
          ))}

          {filteredTickets.length === 0 && (
            <EmptyState
              iconClass="bi bi-ticket"
              title={`No ${activeTab} Tickets`}
              subtitle="You're all caught up!"
              height="80vh"
            />
          )}
        </div>
      </aside>

      {/* Chat Panel */}
      <main
        className="flex-grow-1 d-flex flex-column p-3"
        style={{ height: "93vh", overflow: "hidden", background: "white" }}
      >
        {currentTicket ? (
          <>
            {/* Header */}
            <div className="d-flex justify-content-between align-items-start border-bottom pb-3 mb-3 flex-wrap">
              <div style={{ maxWidth: "40%", flexGrow: 1 }}>
                <h6 className="fw-semibold text-dark mb-1 d-flex flex-wrap align-items-center gap-2">
                  <span className="text-truncate" style={{ maxWidth: "60%" }}>
                    {currentTicket.category} » {currentTicket.subject}
                  </span>

                  <span
                    className={`badge ${
                      currentTicket.status === "Open"
                        ? "bg-success"
                        : "bg-secondary"
                    }`}
                  >
                    {currentTicket.status}
                  </span>
                </h6>
                <div className="text-muted small d-flex flex-wrap gap-2 align-items-center">
                  <span>
                    {new Date(currentTicket.created_at).toLocaleString()}
                  </span>
                  <span>•</span>
                  <span className="text-truncate" style={{ maxWidth: 120 }}>
                    #{currentTicket.id}
                  </span>
                  <i
                    className={`bi bi-copy ${
                      copied ? "text-success" : "text-muted"
                    }`}
                    style={{ cursor: "pointer" }}
                    title={copied ? "Copied!" : "Copy Ticket ID"}
                    onClick={() => copyToClipboard(currentTicket.id)}
                  ></i>
                </div>
              </div>

              <div className="d-flex align-items-center gap-2 mt-2 mt-md-0 flex-shrink-0">
                {openStatuses.includes(currentTicket.status) && (
                  <button
                    className="btn btn-outline-danger btn-sm rounded-pill shadow-sm fw-semibold"
                    onClick={() => handleCloseTicket(currentTicket.id)}
                    disabled={closingTicketId === currentTicket.id}
                    style={{ minWidth: "120px" }}
                  >
                    {closingTicketId === currentTicket.id ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Closing...
                      </>
                    ) : (
                      "Close Ticket"
                    )}
                  </button>
                )}

                {!openStatuses.includes(currentTicket.status) &&
                  (!currentTicket.reviewRating || !currentTicket.reviewRating.submitted) && (
                    <button
                      className="btn btn-success btn-sm rounded-pill shadow-sm fw-semibold"
                      onClick={() => {
                        setReviewTicketId(currentTicket.id);
                        setShowReviewModal(true);
                      }}
                      style={{ minWidth: "120px" }}
                    >
                      Give Review
                    </button>
                )}
              </div>
            </div>

            {/* Messages */}
            <div
              className="bg-white p-3 mb-3 flex-grow-1 overflow-auto"
              ref={messagesContainerRef}
              style={{ minHeight: 0 }}
            >
              {currentTicket.messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`d-flex mb-2 ${
                    msg.sender === "user"
                      ? "justify-content-end"
                      : "justify-content-start"
                  }`}
                >
                  {msg.sender !== "user" && (
                    <img
                      src={msg.avatarUrl || "/default-avatar.png"}
                      alt={`${msg.name || "Support"} avatar`}
                      className="chat-avatar"
                    />
                  )}

                  <div
                    className={`chat-bubble ${
                      msg.sender === "user"
                        ? "chat-bubble-user"
                        : "chat-bubble-support"
                    }`}
                    style={{ maxWidth: "70%" }}
                  >
                    {msg.sender !== "user" && (
                      <div className="chat-sender-name">
                        {msg.name || "Support"}
                      </div>
                    )}
                    <div className="chat-text">{msg.text}</div>
                    <div className="chat-time">{formatTime(msg.timestamp)}</div>
                  </div>

                  {msg.sender === "user" && (
                    <img
                      src={msg.avatarUrl || "/default-user-avatar.png"}
                      alt="You avatar"
                      className="chat-avatar"
                    />
                  )}
                </div>
              ))}
            </div>

            {renderActionsForTicket(currentTicket)}
          </>
        ) : (
          <EmptyState
            iconClass="bi bi-chat-left-dots"
            title="No Ticket Selected"
            subtitle="Select a ticket to view details"
            height="90vh"
          />
        )}
      </main>

      {/* Review Modal */}
      {showReviewModal && (
        <div
          className="modal fade show"
          style={{
            display: "block",
            background: "rgba(0,0,0,0.5)",
            backdropFilter: "blur(3px)",
          }}
        >
          <div className="modal-dialog modal-dialog-centered modal-dialog-centered-support">
            <div className="modal-content border-0 rounded-4 shadow-lg animate__animated animate__fadeInUp">
              <div
                className="modal-header border-0 text-white"
                style={{
                  background: "linear-gradient(90deg, #0d6efd, #4dabf7)",
                  borderTopLeftRadius: "1rem",
                  borderTopRightRadius: "1rem",
                }}
              >
                <h5 className="modal-title fw-bold">
                  <i className="bi bi-star-fill me-2"></i> Rate Our Support
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowReviewModal(false)}
                ></button>
              </div>

              <div className="px-4 py-3">
                <div className="p-3 rounded-4 bg-white shadow-sm d-flex flex-wrap align-items-center gap-4">
                  <div className="border-end pe-4" style={{ minWidth: 150 }}>
                    <small className="text-secondary fw-medium d-block">
                      Ticket Code
                    </small>
                    <span className="fw-semibold">#{reviewTicketId}</span>
                  </div>

                  <div className="border-end pe-4" style={{ minWidth: 200 }}>
                    <small className="text-secondary fw-medium d-block">
                      Category
                    </small>
                    <span className="fw-semibold">
                      {tickets.find((t) => t.id === reviewTicketId)?.category ||
                        "N/A"}
                    </span>
                  </div>

                  <div className="border-end pe-4" style={{ minWidth: 150 }}>
                    <small className="text-secondary fw-medium d-block">
                      Ticket Status
                    </small>
                    <span className="fw-semibold">
                      {tickets.find((t) => t.id === reviewTicketId)?.status ||
                        "N/A"}
                    </span>
                  </div>

                  <div style={{ minWidth: 150 }}>
                    <small className="text-secondary fw-medium d-block">
                      Ticket Priority
                    </small>
                    <span
                      className="px-3 py-1 rounded-pill fw-medium"
                      style={{
                        backgroundColor: "rgba(99, 102, 241, 0.1)",
                        color: "#6366F1",
                        fontSize: "0.85rem",
                      }}
                    >
                      {tickets.find((t) => t.id === reviewTicketId)?.priority ||
                        "Medium"}
                    </span>
                  </div>
                </div>
              </div>

              <div className="modal-body text-center pt-0">
                <div className="mb-4 d-flex justify-content-center">
                  {[1, 2, 3, 4, 5].map((n) => {
                    const isActive = n <= (hoverRating || selectedRating);
                    return (
                      <i
                        key={n}
                        className={`bi ${
                          isActive
                            ? "bi-star-fill text-warning"
                            : "bi-star text-muted"
                        } mx-1`}
                        style={{
                          fontSize: "2.5rem",
                          cursor: "pointer",
                          transition: "transform 0.2s, color 0.2s",
                        }}
                        onMouseEnter={() => setHoverRating(n)}
                        onMouseLeave={() => setHoverRating(0)}
                        onClick={() => setSelectedRating(n)}
                      />
                    );
                  })}
                </div>

                <textarea
                  className="form-control mb-3 rounded-3 shadow-sm border"
                  rows={4}
                  placeholder="Share your experience..."
                  value={reviewComment}
                  onChange={(e) => setReviewComment(e.target.value)}
                  style={{ resize: "none" }}
                />
              </div>

              <div className="modal-footer modal-footer-support border-0 px-4 pb-4">
                <button
                  className="btn btn-outline-secondary rounded-pill px-4"
                  onClick={() => setShowReviewModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary rounded-pill px-4 shadow-sm"
                  onClick={async () => {
                    await handleSubmitReview();
                    setShowReviewModal(false);
                  }}
                  disabled={selectedRating === 0}
                  style={{
                    background: "linear-gradient(90deg, #0d6efd, #4dabf7)",
                    border: "none",
                  }}
                >
                  Submit Review
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShowSupportTickets;